/**
 * Adapter: wires the chosen subscription offering through a two-step
 * checkout — Step 1 sign the agreement, Step 2 the unified `<CheckoutShell>`
 * (payment) in picker-required mode.
 *
 * Responsibilities:
 *   1. Read the chosen selection from SubscriptionCheckoutContext.
 *      If null (refresh / direct nav), bounce back to the landing page.
 *   2. STEP 1 — require the agreement to be signed before payment is shown.
 *      No signature, no charge, and the contract is presented up front.
 *   3. STEP 2 — load saved payment sources, build a Cart, wire
 *      onAddPaymentSource → createPaymentSource and onSubmit →
 *      createSubscription (with the already-signed agreement uuid), then
 *      navigate to the routable confirmation page.
 *
 * What stays domain-specific (NOT in this file):
 *   - Pre-checkout: the landing page owns the marketing copy + picker.
 *   - <SubscriptionConfirmationPage> at /subscriptions/:id/confirmation.
 */

import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";

import { CheckoutShell } from "@/modules/checkout/components/CheckoutShell";
import type {
  AcceptJsToken,
  PaymentSource,
  SubmitInput,
  SubmitResult,
} from "@/modules/checkout/types";
import { listPaymentSources } from "@/modules/checkout/api/listPaymentSources";
import { createPaymentSource } from "@/modules/checkout/api/createPaymentSource";

import { useSubscriptionCheckout } from "../../SubscriptionCheckoutContext";
import { buildSubscriptionCart } from "../../utils/buildSubscriptionCart";
import { createSubscription } from "../../api/createSubscription";
import LoadingIndicator from "@/components/LoadingIndicator/LoadingIndicator";
import { useAppSelector } from "@/app/hooks";
import { selectUserAppSettings } from "@/modules/hub/features/UserAppSettings/selectors/userAppSettingsSelectors";
import { SubscriptionAgreementStep } from "../SubscriptionAgreementStep/SubscriptionAgreementStep";

const ACCEPT_JS_DESCRIPTOR = "COMMON.ACCEPT.INAPP.PAYMENT";

export default function SubscriptionCheckoutShellAdapter() {
  const navigate = useNavigate();
  const { slug } = useParams<{ slug: string }>();
  const { state } = useSubscriptionCheckout();
  const { selection } = state;

  const userAppSettings = useAppSelector(selectUserAppSettings);

  const [paymentSources, setPaymentSources] = useState<PaymentSource[] | null>(
    null,
  );

  // Step 1 gate: the agreement uuid captured by the sign step. Payment
  // (step 2) is not rendered until this is set — no signature, no charge.
  const [signedAgreementUuid, setSignedAgreementUuid] = useState<string | null>(
    null,
  );

  const backToLanding = () =>
    navigate(`/subscriptions/${slug ?? ""}`, { replace: true });

  // Refresh on /checkout (or direct nav) leaves selection null. Bounce
  // back to the landing page where the user can pick again.
  useEffect(() => {
    if (selection === null) {
      navigate(`/subscriptions/${slug ?? ""}`, { replace: true });
    }
  }, [selection, navigate, slug]);

  // Hydrate the saved-card list once (loads in the background while the user
  // is on the sign step). The list grows in-memory after a successful
  // `onAddPaymentSource` save (the shell prepends + selects).
  useEffect(() => {
    if (selection === null) return;
    let cancelled = false;
    listPaymentSources()
      .then((response) => {
        if (!cancelled) setPaymentSources(response.data.paymentSources);
      })
      .catch(() => {
        if (!cancelled) setPaymentSources([]);
      });
    return () => {
      cancelled = true;
    };
  }, [selection]);

  // Pre-bounce: render nothing while the redirect effect runs.
  if (selection === null) return null;

  // STEP 1 — sign the agreement before anything payment-related is shown.
  if (signedAgreementUuid === null) {
    return (
      <SubscriptionAgreementStep
        caraSku={selection.caraSku}
        defaultEmail={userAppSettings?.email ?? ""}
        defaultName={
          userAppSettings
            ? `${userAppSettings.firstName ?? ""} ${userAppSettings.lastName ?? ""}`.trim()
            : ""
        }
        onBack={backToLanding}
        onSigned={setSignedAgreementUuid}
      />
    );
  }

  // STEP 2 — payment. Wait for the saved-card list before rendering the
  // shell so the picker has its real initial state.
  if (paymentSources === null) {
    return <LoadingIndicator />;
  }

  const cart = buildSubscriptionCart({ selection });

  const onAddPaymentSource = async (
    token: AcceptJsToken,
  ): Promise<PaymentSource> => {
    const expiration = (token.cardExpiration ?? "").split("/");
    const expMonth = expiration[0] ?? "";
    const expYearShort = expiration[1] ?? "";
    const expYear =
      expYearShort.length === 2 ? `20${expYearShort}` : expYearShort;

    const response = await createPaymentSource({
      paymentName: token.name !== "" ? token.name : null,
      paymentDescriptor: ACCEPT_JS_DESCRIPTOR,
      paymentToken: token.dataValue,
      paymentExpMonth: expMonth,
      paymentExpYear: expYear,
      paymentLast4: token.last4,
    });
    return response.data;
  };

  const onSubmit = async (input: SubmitInput): Promise<SubmitResult> => {
    if (input.payment?.kind !== "saved") {
      throw new Error("Subscription checkout requires a saved card.");
    }
    if (signedAgreementUuid === null) {
      throw new Error("The subscription agreement must be signed first.");
    }

    const response = await createSubscription({
      paymentSourceId: input.payment.paymentSourceId,
      caraSku: selection.caraSku,
      frequency: selection.frequency,
      signedAgreementUuid,
    });

    const successPath = `/subscriptions/${slug ?? ""}/confirmation/${response.data.subscriptionUuid}`;
    navigate(successPath);

    return {
      confirmationNumber: response.data.subscriptionId,
      successPath,
    };
  };

  return (
    <div className="space-y-4">
      <div className="rounded-md border border-gray-200 dark:border-slate-700 bg-gray-50 dark:bg-slate-800 px-4 py-3 text-sm text-gray-600 dark:text-gray-300">
        ✓ Agreement signed. Complete payment below to start your subscription.
      </div>

      <CheckoutShell
        cart={cart}
        onAddPaymentSource={onAddPaymentSource}
        onSubmit={onSubmit}
        paymentMode="picker-required"
        paymentSources={paymentSources}
      />
    </div>
  );
}
